import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  getEffectiveKeymap,
  getKeybindingLayoutMap,
  subscribeEffectiveKeymap,
} from '../../features/keybindings/dispatch/effectiveKeymap';
import { platform } from '../../features/keybindings/model/platform';
import type { Keymap } from '../../features/keybindings/model/keybindingTypes';
import { useAppStore } from '../../store/appStore';
import { appMenuAccelerators } from './appMenuAccelerators';
import { hiddenMenuCommandIds } from './appMenuBetaGating';

export type RendererAppMenuConfig = {
  readonly hiddenCommandIds: readonly string[];
  readonly accelerators: ReadonlyMap<string, string>;
};

/**
 * How long the first push waits for a keyboard-layout map that is still
 * loading. Chromium answers within milliseconds; this only bounds a request
 * that never settles, so the menu cannot stay unsynced for good.
 */
export const APP_MENU_LAYOUT_WAIT_MS = 3000;

export function buildRendererAppMenuConfig(
  keymap: Keymap,
  hiddenCommandIds: readonly string[],
): RendererAppMenuConfig {
  return { hiddenCommandIds, accelerators: appMenuAccelerators(keymap, platform()) };
}

/**
 * Keeps the native menu's visibility and shortcut labels aligned to renderer state.
 *
 * Nothing is pushed while the keyboard-layout map is loading. A keymap with a
 * physical-key binding (`Ctrl+[KeyB]`) can resolve differently before and
 * after the map arrives, and each different push rebuilds the menu of the live
 * window, which on Linux costs a compositor frame per keystroke until the next
 * launch (electron/appMenu.ts). Until the map settles ('ready' or
 * 'unavailable'), or APP_MENU_LAYOUT_WAIT_MS passes, the menu main built from
 * the config it saved on the previous launch stays in place.
 */
export function useAppMenuConfigSync(): void {
  const betaFeatures = useAppStore((state) => state.betaFeatures);
  const keymap = useSyncExternalStore(
    subscribeEffectiveKeymap,
    getEffectiveKeymap,
    getEffectiveKeymap,
  );
  const layoutMap = getKeybindingLayoutMap();
  const layout = useSyncExternalStore(layoutMap.subscribe, layoutMap.getSnapshot, layoutMap.getSnapshot);
  const layoutLoading = layout.status === 'loading';
  const [layoutWaitOver, setLayoutWaitOver] = useState(false);

  useEffect(() => {
    if (!layoutLoading) return;
    const timer = setTimeout(() => setLayoutWaitOver(true), APP_MENU_LAYOUT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [layoutLoading]);

  const canPush = !layoutLoading || layoutWaitOver;
  useEffect(() => {
    if (!canPush) return;
    const menuConfig = buildRendererAppMenuConfig(
      keymap,
      hiddenMenuCommandIds(betaFeatures),
    );
    void window.electronAPI?.appMenu?.setConfig?.(menuConfig)?.catch?.(() => {});
  }, [betaFeatures, keymap, canPush]);
}
